import React, { ReactElement, useEffect } from 'react';
import {
    WEBSOCKET_ACTIONS,
    CalculationFailedMessage,
    WebsocketWorkerMessageEvent,
    CalculationProgressMessage,
    CalculationReceivedMessage
} from '../websocket/WebsocketWorker.types';
import { STORAGE_CALC_JOB_ID_KEY } from '../common/constants';
import EventStore from '../websocket/eventStore';
import { removeCalcProgressFromSessionStorage } from '../common/storage.functions';
import {
    getCalculationStatusFromResult,
    calculationProgressHandler
} from './calculation.functions';
import {
    CalculationResult,
    CalculationStatus
} from './CalculationResult.types';

export interface CalculationResultChangeEvent {
    result?: CalculationResult;
    jobId: string;
    status: CalculationStatus;
    message?: string;
}

interface CalculationEventWrapperProps {
    ahuCalculationStatus: CalculationStatus;
    eventStore: EventStore | undefined;
    children: ReactElement;
    onCalculationResultChanged: (event: CalculationResultChangeEvent) => void;
}

const CalculationEventWrapper: React.FC<CalculationEventWrapperProps> = ({
    ahuCalculationStatus,
    eventStore,
    children,
    onCalculationResultChanged
}) => {
    const calculationResultReceivedHandler = (
        message: CalculationReceivedMessage
    ) => {
        const jobId = sessionStorage.getItem(STORAGE_CALC_JOB_ID_KEY);
        if (!jobId || jobId !== message.jobId) {
            return;
        }
        removeCalcProgressFromSessionStorage();
        const calulcationResultStatus = getCalculationStatusFromResult(
            message.calculationResult
        );
        if (ahuCalculationStatus === 'CALC_RUNNING') {
            onCalculationResultChanged({
                result: message.calculationResult,
                status: calulcationResultStatus,
                jobId: message.jobId
            });
        } else {
            console.log(
                'Unit was re-assembled during calculation, discarding the results.'
            );
        }
    };

    const calculationFailedHandler = (message: CalculationFailedMessage) => {
        const jobId = sessionStorage.getItem(STORAGE_CALC_JOB_ID_KEY);

        if (!jobId || jobId !== message.jobId) {
            return;
        }

        if (ahuCalculationStatus === 'CALC_RUNNING') {
            removeCalcProgressFromSessionStorage();
            onCalculationResultChanged({
                result: undefined,
                status: 'CALC_RES_ERROR',
                jobId: message.jobId,
                message: message.message
            });
        } else {
            console.log(
                'Unit was re-assembled during calculation, discarding the results.'
            );
        }
    };

    const listenerCallback = (event: WebsocketWorkerMessageEvent) => {
        const { task, message } = event.data;

        if (task === WEBSOCKET_ACTIONS.CALCRESULTS_RECEIVED) {
            calculationResultReceivedHandler(
                message as CalculationReceivedMessage
            );
        }
        if (task === WEBSOCKET_ACTIONS.CALCULATION_FAILED) {
            calculationFailedHandler(message as CalculationFailedMessage);
        }
        if (task === WEBSOCKET_ACTIONS.CALCULATION_PROGRESS) {
            calculationProgressHandler(message as CalculationProgressMessage);
        }
    };

    useEffect(() => {
        if (!eventStore) {
            return;
        }

        const listenerName = 'CalculationEventWrapper';
        eventStore.addEventListener(listenerName, listenerCallback);
        return () => {
            eventStore.removeEventListener(listenerName);
        };
    });

    return <>{children}</>;
};

export default CalculationEventWrapper;
